import React from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '../Button';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: '#FFFFFF',
  },
  container: {
    maxWidth: '896px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(8),
    animation: '$fadeIn 0.6s ease-out',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: '#121212',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      fontSize: '3rem',
    },
  },
  accordionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  accordion: {
    border: '2px solid #F5F5F5',
    borderRadius: '8px !important',
    boxShadow: 'none',
    transition: 'all 0.3s ease',
    '&:before': {
      display: 'none',
    },
    '&:hover': {
      borderColor: '#D91A5B',
    },
    '&.Mui-expanded': {
      margin: '0 !important',
    },
  },
  accordionSummary: {
    padding: theme.spacing(3),
    minHeight: 'auto !important',
    '&.Mui-expanded': {
      minHeight: 'auto !important',
    },
    '& .MuiAccordionSummary-content': {
      margin: '0 !important',
    },
  },
  question: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#121212',
    textAlign: 'left',
  },
  accordionDetails: {
    padding: theme.spacing(0, 3, 3, 3),
  },
  answer: {
    color: '#616161',
    lineHeight: 1.7,
  },
  ctaContainer: {
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#121212',
    marginBottom: theme.spacing(2),
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const faqs = [
  {
    question: 'Can I make science diagrams for free?',
    answer:
      'Yes! Mind The Graph offers a free tier that includes access to thousands of illustrations, templates, and basic editing tools. You can create and download diagrams at no cost. For advanced features like watermark-free exports and AI generation, consider upgrading to Prime.',
  },
  {
    question: 'What export formats are supported?',
    answer:
      'You can export your diagrams in multiple formats including PNG, PDF, SVG, and TIFF (up to 1200 DPI). Prime users get access to watermark-free exports in all formats, perfect for journal submissions and presentations.',
  },
  {
    question: 'Can I collaborate with others on my diagrams?',
    answer:
      'Yes! Our platform supports collaborative editing. You can invite team members to work on diagrams together, making it perfect for research teams and co-authored publications.',
  },
  {
    question: 'Can I customize the scientific symbols and illustrations?',
    answer:
      'Absolutely! Our PowerEdit tools let you fully customize colors, sizes, positions, and layers. You can also request custom illustrations from our expert design team if you need something specific.',
  },
  {
    question: 'Can I use it across different devices?',
    answer:
      'Yes! Mind The Graph is a web-based platform that works seamlessly across desktop, tablet, and mobile devices. Your work is automatically saved to the cloud, so you can access it from anywhere.',
  },
  {
    question: 'Do I own the rights to my diagrams?',
    answer:
      'Prime users receive full copyright and publication rights for their exported diagrams. You can use them in journal articles, grant proposals, conference presentations, and any other academic or commercial purposes without restrictions.',
  },
];

export default function FAQ() {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const handleCreateNowClick = () => {
    window.location.href = getRedirectUrlAfterLogin();
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <section className={classes.section}>
      <Container maxWidth={false} className={classes.container}>
        <Box className={classes.header}>
          <Typography className={classes.title} component="h2">
            Frequently Asked Questions
          </Typography>
        </Box>

        <Box className={classes.accordionContainer}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              className={classes.accordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Typography className={classes.question} component="h3">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Typography className={classes.answer}>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box className={classes.ctaContainer}>
          <Typography className={classes.ctaTitle}>
            Ready to Create Your Next Scientific Diagram?
          </Typography>
          <Button size="large" variant="contained" onClick={handleCreateNowClick} component="a">
            Create Now – It's Free
          </Button>
        </Box>
      </Container>
    </section>
  );
}
